import { LitElement, html, css, t } from '@hh/global.js'
import recentlyViewedStorage from './recently-viewed-storage.js'

// The tab grid is a single row of four cards, so never render more than that.
const RECENTLY_VIEWED_LIMIT = 4

class HHProductRecs extends LitElement {
  static properties = {
    productId: { type: String, attribute: 'product-id' },
    sectionId: { type: String, attribute: 'section-id' },
    productHandle: { type: String, attribute: 'product-handle' },
    rvMinimumProducts: { type: Number, attribute: 'rv-minimum-products' },
    productRecs: { type: String, state: true },
    productsComp: { type: String, state: true },
    recentlyViewedHandles: { type: Array, state: true },
  }

  constructor() {
    super()
    this.recentlyViewedHandles = []
    this._tabNavAbort = null
  }

  connectedCallback() {
    super.connectedCallback()
    this.loadRecentlyViewedHandles()
    this.fetchData()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this._tabNavAbort?.abort()
  }

  loadRecentlyViewedHandles() {
    const handles = recentlyViewedStorage
      .list()
      .filter((handle) => handle && handle !== this.productHandle)

    this.recentlyViewedHandles =
      handles.length >= this.rvMinimumProducts
        ? handles.slice(0, RECENTLY_VIEWED_LIMIT)
        : []

    this.populateRecentlyViewed()
  }

  populateRecentlyViewed() {
    const mount = this.querySelector('[data-recently-viewed-mount]')
    if (!mount) return

    if (this.recentlyViewedHandles.length === 0) {
      mount.replaceChildren()
      return
    }

    const list = document.createElement('ul')
    list.className = 'rec-product__list'
    list.setAttribute('aria-label', t('products.product.recently_viewed'))

    this.recentlyViewedHandles.forEach((handle) => {
      const item = document.createElement('li')
      const card = document.createElement('dynamic-product-card')
      card.setAttribute('handle', handle)
      card.setAttribute('template', 'product-card')
      item.appendChild(card)
      list.appendChild(item)
    })

    mount.replaceChildren(list)
  }

  getRecommendationsRoot() {
    const country = window.Shopify?.country
    const useRoot =
      country == 'AE' ||
      country == 'SA' ||
      country == 'QA' ||
      country == 'FR' ||
      country == 'MX' ||
      country == 'ES' ||
      country == 'BR' ||
      country == 'CN'

    return useRoot ? window.routes.root_url : ''
  }

  getVisibleTabs() {
    const tabs = []

    if (this.recentlyViewedHandles.length > 0) {
      tabs.push({
        id: 'rv',
        tabId: `rv-tab-${this.sectionId}`,
        panelId: `rv-panel-${this.sectionId}`,
        label: t('products.product.recently_viewed'),
        type: 'slot',
        slotName: 'product-rv',
      })
    }

    if (this.productRecs) {
      tabs.push({
        id: 'ymal',
        tabId: `ymal-tab-${this.sectionId}`,
        panelId: `ymal-panel-${this.sectionId}`,
        label: t('products.product.you_may_also_like'),
        type: 'slot',
        slotName: 'product-recs',
      })
    }

    if (this.productsComp) {
      tabs.push({
        id: 'comp',
        tabId: `comp-tab-${this.sectionId}`,
        panelId: `comp-panel-${this.sectionId}`,
        label: t('products.product.styled_with'),
        type: 'slot',
        slotName: 'product-comps',
      })
    }

    return tabs
  }

  async fetchData() {
    const root = this.getRecommendationsRoot()

    this.productRecs = await fetch(
      `${root}/recommendations/products?limit=4&product_id=${this.productId}&section_id=${this.sectionId}`
    )
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div')
        html.innerHTML = text
        const recommendations = html.querySelector(
          `.js-product-recommendations[data-type='recommended']`
        )

        if (recommendations?.innerHTML.trim().length) {
          return recommendations.innerHTML
        }
      })
      .catch((e) => {
        console.error(e)
      })

    this.productsComp = await fetch(
      `${root}/recommendations/products?limit=4&intent=complementary&product_id=${this.productId}&section_id=${this.sectionId}`
    )
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div')
        html.innerHTML = text
        const recommendations = html.querySelector(
          `.js-product-recommendations[data-type='complementary']`
        )

        if (recommendations?.innerHTML.trim().length > 0) {
          return recommendations.innerHTML
        }

        return null
      })
      .catch((e) => {
        console.error(e)
      })

    if (this.productRecs) {
      document.querySelector(
        '.js-product-recommendations[data-type="recommended"]'
      ).innerHTML = this.productRecs
    }

    if (this.productsComp) {
      document.querySelector(
        '.js-product-recommendations[data-type="complementary"]'
      ).innerHTML = this.productsComp
    }

    this.requestUpdate()

    document
      .querySelectorAll('[data-featured-link]')
      .forEach((nonAnchorLink) => {
        nonAnchorLink.addEventListener('click', () => {
          const link = nonAnchorLink.getAttribute('data-featured-link')
          window.location.href = link
        })
      })

    this.setupTabNavigation()
  }

  setupTabNavigation() {
    this._tabNavAbort?.abort()
    this._tabNavAbort = new AbortController()
    const { signal } = this._tabNavAbort

    requestAnimationFrame(() => {
      const tabs = this.shadowRoot?.querySelectorAll('.js-tab')
      if (!tabs || tabs.length === 0) return

      tabs.forEach((tab, index) => {
        tab.addEventListener(
          'keydown',
          (e) => {
            let newIndex = index

            if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
              e.preventDefault()
              newIndex = (index + 1) % tabs.length
            } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
              e.preventDefault()
              newIndex = (index - 1 + tabs.length) % tabs.length
            } else if (e.key === 'Home') {
              e.preventDefault()
              newIndex = 0
            } else if (e.key === 'End') {
              e.preventDefault()
              newIndex = tabs.length - 1
            } else if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              this.activateTab(tabs, index)
              return
            } else {
              return
            }

            this.activateTab(tabs, newIndex)
          },
          { signal }
        )

        tab.addEventListener(
          'click',
          () => {
            this.activateTab(tabs, index)
          },
          { signal }
        )
      })
    })
  }

  activateTab(tabs, newIndex) {
    tabs.forEach((tab, i) => {
      const isSelected = i === newIndex
      tab.setAttribute('aria-selected', isSelected.toString())
      tab.classList.toggle('active', isSelected)
      tab.setAttribute('tabindex', isSelected ? '0' : '-1')

      const panelId = tab.getAttribute('aria-controls')
      const panel = this.shadowRoot?.querySelector(`#${panelId}`)
      if (panel) {
        panel.classList.toggle('hidden', !isSelected)
      }
    })

    tabs[newIndex].focus()
  }

  render() {
    const tabs = this.getVisibleTabs()

    if (tabs.length === 0) {
      return html``
    }

    const landmarkId = `rec-landmark-${this.sectionId}`

    if (tabs.length === 1) {
      const tab = tabs[0]
      const headingId = `${tab.id}-heading-${this.sectionId}`

      return html`
        <div
          class="rec-products container"
          role="region"
          aria-labelledby="${headingId}"
        >
          <h2 class="utility-2" id="${headingId}">${tab.label}</h2>
          <slot name="${tab.slotName}"></slot>
        </div>
      `
    }

    return html`
      <hh-tabs>
        <div
          class="rec-products container"
          role="region"
          aria-labelledby="${landmarkId}"
        >
          <h2 class="visually-hidden" id="${landmarkId}">
            ${t('products.product.recommendations_heading')}
          </h2>
          <div
            class="js-tablist"
            role="tablist"
            aria-label="${t('products.product.recommendations_heading')}"
          >
            ${tabs.map(
              (tab, index) => html`
                <button
                  id="${tab.tabId}"
                  type="button"
                  class="rec-product__tab-button utility-2 js-tab ${index === 0
                    ? 'active'
                    : ''}"
                  role="tab"
                  tabindex="${index === 0 ? '0' : '-1'}"
                  aria-selected="${index === 0 ? 'true' : 'false'}"
                  aria-controls="${tab.panelId}"
                >
                  ${tab.label}
                </button>
              `
            )}
          </div>

          <div class="flex rec-product__tab-content">
            ${tabs.map(
              (tab, index) => html`
                <div
                  id="${tab.panelId}"
                  class="js-tabpanel ${index === 0 ? '' : 'hidden'}"
                  role="tabpanel"
                  aria-labelledby="${tab.tabId}"
                  tabindex="-1"
                >
                  <slot name="${tab.slotName}"></slot>
                </div>
              `
            )}
          </div>
        </div>
      </hh-tabs>
    `
  }

  static styles = [
    css`
      :host *,
      :host *::before,
      :host *::after {
        box-sizing: border-box;
      }
      .hidden {
        display: none;
      }

      .visually-hidden {
        position: absolute !important;
        overflow: hidden;
        width: 1px;
        height: 1px;
        margin: -1px;
        padding: 0;
        border: 0;
        clip: rect(0 0 0 0);
        word-wrap: normal !important;
      }
      .rec-products {
        align-items: center;
        display: flex;
        flex-direction: column;
        gap: var(--hh-space-sm);
        padding-top: var(--hh-space-xl);
        padding-bottom: var(--hh-space-xl);
      }

      .rec-product__list {
        display: grid;
        gap: var(--hh-page-gutter);
        grid-template-columns: 1fr 1fr;
      }

      @media screen and (min-width: 990px) {
        .rec-product__list {
          grid-template-columns: 1fr 1fr 1fr 1fr;
        }
      }

      .rec-product__tab-button.js-tab {
        background: transparent;
        border: none;
        border-bottom: 1px solid transparent;
        color: var(--hh-color-subdued);
        cursor: pointer;
        text-transform: capitalize;
        padding: 0 0 2px;
        margin: 0 6px;
      }

      .rec-product__tab-button.js-tab:focus-visible {
        outline: 2px solid var(--hh-color-focus, #000);
        outline-offset: 2px;
      }

      .rec-product__tab-content {
        width: 100%;
      }

      .rec-product__tab-button.active {
        color: var(--hh-color-ink);
        border-bottom: 1px solid var(--hh-color-ink);
      }

      .utility-2 {
        font: var(--hh-utility-2);
        letter-spacing: var(--hh-utility-2-letter-spacing);
        margin: 0;
        text-transform: var(--hh-utility-text-transform);
      }

      .container {
        width: 100%;
        max-width: 144rem;
        margin: 0 auto;
        box-sizing: border-box;
        padding-left: var(--hh-space-page-margins);
        padding-right: var(--hh-space-page-margins);
      }
    `,
  ]
}

customElements.define('hh-product-recs', HHProductRecs)
